import type { Stylist } from '@/types'

import Link from 'next/link'
import { SmartImage } from '@/components/site/smart-image'
import { cms } from '@/lib/cms'
import { cn } from '@/lib/utils'

interface StylistCardProps {
    stylist: Stylist
    showBio?: boolean
    className?: string
}

export const StylistCard = ({ stylist, showBio = false, className }: StylistCardProps) => (
    <article className={cn('group flex flex-col', className)}>
        <div className='relative aspect-[4/5] overflow-hidden rounded-3xl bg-muted'>
            <SmartImage
                src={stylist.image}
                alt={stylist.image_alt}
                sizes='(min-width: 1280px) 300px, (min-width: 640px) 33vw, 50vw'
                className='object-top transition duration-700 group-hover:scale-105'
            />
            <div aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-navy-950/75 via-transparent to-transparent opacity-80' />
            <div className='absolute inset-x-4 bottom-4 flex flex-wrap gap-1.5'>
                {stylist.specialties.slice(0, 2).map(specialty => (
                    <span key={specialty} className='rounded-full bg-cream-50/90 px-2.5 py-1 text-[0.6875rem] font-semibold text-navy-900 backdrop-blur'>
                        {specialty}
                    </span>
                ))}
            </div>
        </div>

        <div className='mt-4 px-1'>
            <h3 className='font-heading text-2xl leading-tight font-semibold text-navy-900'>
                <span data-mdlfy-cms={cms('stylists', stylist.id, 'name')}>{stylist.name}</span>
            </h3>
            {stylist.role && (
                <p data-mdlfy-cms={cms('stylists', stylist.id, 'role')} className='mt-1 text-sm text-emerald-700'>
                    {stylist.role}
                </p>
            )}
            <p className='mt-1 text-xs tracking-wide text-muted-foreground'>
                <span>{stylist.experience_years}</span> years of experience
            </p>
            {showBio && stylist.bio && (
                <p data-mdlfy-cms={cms('stylists', stylist.id, 'bio')} className='mt-3 text-sm leading-relaxed text-muted-foreground'>
                    {stylist.bio}
                </p>
            )}
            <Link
                href={`/book?stylist=${stylist.slug}`}
                className='mt-3 inline-flex text-sm font-semibold text-navy-900 underline decoration-gold-400 decoration-2 underline-offset-[6px] transition hover:text-emerald-800'
            >
                Book with <span className='ml-1'>{stylist.name.split(' ')[0]}</span>
            </Link>
        </div>
    </article>
)
